<template>
  <div class="nutritionist-dashboard">
    <!-- Header -->
    <div class="dashboard-header">
      <h1 class="dashboard-title">NUTRITION</h1>
      <p class="dashboard-subtitle">Analyse & Optimisation</p>
    </div>

    <!-- Cartes principales -->
    <div class="cards-grid">
      <!-- Card 1: Équilibre nutritionnel -->
      <div class="card card-featured" @click="showNutritionalBalance = true">
        <div class="card-header">
          <h3 class="card-title">📊 Équilibre semaine</h3>
          <span class="badge badge-green">En cours</span>
        </div>
        <p class="card-description">Suivi des apports nutritionnels de la semaine</p>
        <button class="card-link">Consulter →</button>
      </div>

      <!-- Card 2: Alertes -->
      <div class="card" @click="showAlerts = true">
        <div class="card-header">
          <h3 class="card-title">⚠️ Alertes</h3>
          <span class="badge badge-orange">1</span>
        </div>
        <p class="card-description">Points d'attention nutritionnels</p>
        <button class="card-link">Voir →</button>
      </div>

      <!-- Card 3: Acceptation par catégorie -->
      <div class="card" @click="showAcceptance = true">
        <div class="card-header">
          <h3 class="card-title">🍽️ Acceptation</h3>
        </div>
        <p class="card-description">Taux d'appréciation par type d'aliment</p>
        <button class="card-link">Analyser →</button>
      </div>

      <!-- Card 4: Suggestions -->
      <div class="card" @click="showSuggestions = true">
        <div class="card-header">
          <h3 class="card-title">💡 Suggestions</h3>
          <span class="badge badge-blue">3</span>
        </div>
        <p class="card-description">Recommandations d'amélioration des menus</p>
        <button class="card-link">Voir →</button>
      </div>

      <!-- Card 5: Planification menus -->
      <div class="card" @click="showMenuPlanning = true">
        <div class="card-header">
          <h3 class="card-title">🗓️ Planification</h3>
        </div>
        <p class="card-description">Composer les menus équilibrés</p>
        <button class="card-link">Planifier →</button>
      </div>

      <!-- Card 6: Rapports détaillés -->
      <div class="card" @click="showReports = true">
        <div class="card-header">
          <h3 class="card-title">📈 Rapports</h3>
        </div>
        <p class="card-description">Analyses complètes et exports</p>
        <button class="card-link">Générer →</button>
      </div>
    </div>

    <!-- Modal: Équilibre nutritionnel -->
    <div v-if="showNutritionalBalance" class="modal-overlay" @click="showNutritionalBalance = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showNutritionalBalance = false">✕</button>
        <h2 class="modal-title">📊 Équilibre semaine en cours</h2>
        <p class="modal-subtitle">Du 5 au 9 janvier 2026</p>

        <div class="balance-list">
          <div v-for="(item, idx) in nutritionBalance" :key="idx" class="balance-item">
            <div class="balance-header">
              <span class="balance-name">{{ item.name }}</span>
              <span class="balance-percent">{{ item.percent }}%</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :class="item.color" :style="{ width: item.percent + '%' }" />
            </div>
            <div class="balance-status">
              <span v-if="item.percent >= 80" class="status-good">✓ Objectif atteint</span>
              <span v-else-if="item.percent >= 50" class="status-ok">→ Correct</span>
              <span v-else class="status-low">⚠️ Insuffisant</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Alertes -->
    <div v-if="showAlerts" class="modal-overlay" @click="showAlerts = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showAlerts = false">✕</button>
        <h2 class="modal-title">⚠️ Alertes nutritionnelles</h2>

        <div class="alert-list">
          <div class="alert-card alert-warning">
            <div class="alert-icon">🐟</div>
            <div class="alert-content">
              <h4 class="alert-title">Manque de poisson</h4>
              <p class="alert-description">
                Aucun plat de poisson prévu cette semaine. Objectif : 1 fois par semaine minimum.
              </p>
              <button class="alert-action">Ajouter au menu</button>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">Recommandations</h3>
          <ul class="recommendation-list">
            <li>Intégrer un plat de poisson jeudi ou vendredi</li>
            <li>Privilégier les poissons blancs (colin, cabillaud)</li>
            <li>Alternative : poisson pané amélioré</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Modal: Acceptation -->
    <div v-if="showAcceptance" class="modal-overlay" @click="showAcceptance = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showAcceptance = false">✕</button>
        <h2 class="modal-title">🍽️ Acceptation par catégorie</h2>
        <p class="modal-subtitle">Moyenne du dernier mois</p>

        <div class="acceptance-list">
          <div v-for="(item, idx) in acceptance" :key="idx" class="acceptance-item">
            <div class="acceptance-category">
              <span class="category-emoji">{{ item.emoji }}</span>
              <span class="category-name">{{ item.name }}</span>
            </div>
            <div class="acceptance-score">
              <div class="score-bar">
                <div
                  class="score-fill"
                  :class="item.rating >= 70 ? 'bg-green' : 'bg-orange'"
                  :style="{ width: item.rating + '%' }"
                />
              </div>
              <span class="score-value" :class="item.rating >= 70 ? 'text-green' : 'text-orange'">
                {{ item.rating }}%
              </span>
            </div>
          </div>
        </div>

        <div class="insight-box">
          <strong>💡 Insight</strong>
          <p>Les légumineuses et féculents sont bien acceptés. Focus sur les légumes verts et poissons.</p>
        </div>
      </div>
    </div>

    <!-- Modal: Suggestions -->
    <div v-if="showSuggestions" class="modal-overlay" @click="showSuggestions = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showSuggestions = false">✕</button>
        <h2 class="modal-title">💡 Suggestions</h2>

        <div class="suggestion-list">
          <div class="suggestion-card">
            <div class="suggestion-icon">🐟</div>
            <div class="suggestion-content">
              <h4 class="suggestion-title">Intégrer poisson pané amélioré</h4>
              <p class="suggestion-description">
                Suite aux retours négatifs sur le poisson pané (trop sec), tester une nouvelle recette plus moelleuse.
              </p>
              <div class="suggestion-meta">
                <span class="suggestion-impact">Impact élevé</span>
                <span class="suggestion-priority">Prioritaire</span>
              </div>
            </div>
          </div>

          <div class="suggestion-card">
            <div class="suggestion-icon">🥦</div>
            <div class="suggestion-content">
              <h4 class="suggestion-title">Tester brocolis sauce fromagère</h4>
              <p class="suggestion-description">
                Les légumes avec sauce obtiennent de meilleurs scores. Proposition de brocolis gratinés.
              </p>
              <div class="suggestion-meta">
                <span class="suggestion-impact">Impact moyen</span>
              </div>
            </div>
          </div>

          <div class="suggestion-card">
            <div class="suggestion-icon">🍚</div>
            <div class="suggestion-content">
              <h4 class="suggestion-title">Varier les féculents</h4>
              <p class="suggestion-description">
                Alterner riz, pâtes, semoule, pommes de terre et légumineuses pour plus de diversité.
              </p>
              <div class="suggestion-meta">
                <span class="suggestion-impact">Impact faible</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Planification -->
    <div v-if="showMenuPlanning" class="modal-overlay" @click="showMenuPlanning = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showMenuPlanning = false">✕</button>
        <h2 class="modal-title">🗓️ Planification des menus</h2>

        <div class="planning-week">
          <div class="week-selector">
            <button class="week-btn">← Semaine précédente</button>
            <span class="week-label">Semaine du 12 janvier</span>
            <button class="week-btn">Semaine suivante →</button>
          </div>

          <div class="days-grid">
            <div v-for="day in ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi']" :key="day" class="day-card">
              <h4 class="day-name">{{ day }}</h4>
              <div class="menu-list">
                <div class="menu-item">
                  <span class="menu-label">Entrée</span>
                  <span class="menu-value">Taboulé</span>
                </div>
                <div class="menu-item">
                  <span class="menu-label">Plat principal</span>
                  <span class="menu-value">Sauté de porc* sauce pruneaux</span>
                </div>
                <div class="menu-item">
                  <span class="menu-label">Accompagnement</span>
                  <span class="menu-value">Carottes vichy</span>
                </div>
                <div class="menu-item">
                  <span class="menu-label">Produit laitier</span>
                  <span class="menu-value">Yaourt nature BIO</span>
                </div>
                <div class="menu-item">
                  <span class="menu-label">Dessert</span>
                  <span class="menu-value">Compote poire</span>
                </div>
                <div class="menu-item">
                  <span class="menu-label">Sans viande</span>
                  <span class="menu-value">Colin sauce pruneaux</span>
                </div>
              </div>
              <div class="day-balance">
                <span class="balance-indicator incomplete">⚠️ À compléter</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Rapports -->
    <div v-if="showReports" class="modal-overlay" @click="showReports = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showReports = false">✕</button>
        <h2 class="modal-title">📈 Rapports détaillés</h2>

        <div class="report-list">
          <div class="report-card">
            <div class="report-info">
              <h4 class="report-title">Bilan nutritionnel mensuel</h4>
              <p class="report-description">Analyse complète de décembre 2025</p>
            </div>
            <button class="btn-download">📥 Télécharger</button>
          </div>

          <div class="report-card">
            <div class="report-info">
              <h4 class="report-title">Taux d'acceptation par école</h4>
              <p class="report-description">Comparatif des 8 écoles du réseau</p>
            </div>
            <button class="btn-download">📥 Télécharger</button>
          </div>

          <div class="report-card">
            <div class="report-info">
              <h4 class="report-title">Évolution du gaspillage</h4>
              <p class="report-description">Tendances sur 6 mois</p>
            </div>
            <button class="btn-download">📥 Télécharger</button>
          </div>
        </div>

        <button class="btn btn-primary">Générer un nouveau rapport</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "NutritionistDashboard2",
  data() {
    return {
      showNutritionalBalance: false,
      showAlerts: false,
      showAcceptance: false,
      showSuggestions: false,
      showMenuPlanning: false,
      showReports: false,
      nutritionBalance: [
        { name: "Protéines", percent: 80, color: "bg-red" },
        { name: "Légumes", percent: 70, color: "bg-green" },
        { name: "Féculents", percent: 100, color: "bg-yellow" },
        { name: "Produits laitiers", percent: 50, color: "bg-blue" },
      ],
      acceptance: [
        { name: "Légumes verts", rating: 65, emoji: "🥦" },
        { name: "Poissons", rating: 58, emoji: "🐟" },
        { name: "Légumineuses", rating: 72, emoji: "🫘" },
        { name: "Féculents", rating: 88, emoji: "🍚" },
      ],
    }
  },
}
</script>

<style scoped>
.nutritionist-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

.card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.card-featured {
  border: 2px solid #14b8a6;
  background: linear-gradient(135deg, #f0fdfa 0%, white 100%);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.card-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1rem 0;
}

.card-link {
  font-size: 0.875rem;
  color: #2563eb;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
}

.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}
.badge-orange {
  background: #fed7aa;
  color: #9a3412;
}
.badge-green {
  background: #d1fae5;
  color: #065f46;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 800px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Balance */
.balance-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.balance-item {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 12px;
}

.balance-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.balance-name {
  font-weight: 600;
  color: #1a1a1a;
}

.balance-percent {
  font-weight: 700;
  color: #6b7280;
}

.progress-bar {
  height: 12px;
  background: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  transition: width 0.3s;
}

.bg-red {
  background: #ef4444;
}
.bg-green {
  background: #22c55e;
}
.bg-yellow {
  background: #eab308;
}
.bg-blue {
  background: #3b82f6;
}
.bg-orange {
  background: #f97316;
}

.balance-status {
  font-size: 0.75rem;
  font-weight: 600;
}

.status-good {
  color: #22c55e;
}
.status-ok {
  color: #eab308;
}
.status-low {
  color: #f97316;
}

/* Alerts */
.alert-list {
  margin-bottom: 1.5rem;
}

.alert-card {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 12px;
  border-left: 4px solid;
}

.alert-warning {
  background: #fef3c7;
  border-color: #f59e0b;
}

.alert-icon {
  font-size: 2.5rem;
}

.alert-content {
  flex: 1;
}

.alert-title {
  font-size: 1rem;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 0.5rem 0;
}

.alert-description {
  font-size: 0.875rem;
  color: #78350f;
  margin: 0 0 0.75rem 0;
}

.alert-action {
  background: #f59e0b;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.75rem 0;
}

.recommendation-list {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.6;
  padding-left: 1.25rem;
}

/* Acceptance */
.acceptance-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.acceptance-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.acceptance-category {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.category-emoji {
  font-size: 2rem;
}

.category-name {
  font-weight: 500;
  color: #1a1a1a;
}

.acceptance-score {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 150px;
}

.score-bar {
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
}

.score-fill {
  height: 100%;
}

.score-value {
  font-weight: 700;
  min-width: 45px;
  text-align: right;
}

.text-green {
  color: #22c55e;
}
.text-orange {
  color: #f97316;
}

.insight-box {
  background: #eff6ff;
  border-left: 4px solid #2563eb;
  padding: 1rem;
  border-radius: 8px;
}

.insight-box strong {
  display: block;
  color: #1e40af;
  margin-bottom: 0.5rem;
}

.insight-box p {
  font-size: 0.875rem;
  color: #1e40af;
  margin: 0;
}

/* Suggestions */
.suggestion-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.suggestion-card {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: #f9fafb;
  border-radius: 12px;
}

.suggestion-icon {
  font-size: 2.5rem;
}

.suggestion-content {
  flex: 1;
}

.suggestion-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.suggestion-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 0.75rem 0;
}

.suggestion-meta {
  display: flex;
  gap: 0.5rem;
}

.suggestion-impact,
.suggestion-priority {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
}

.suggestion-impact {
  background: #dbeafe;
  color: #1e40af;
}

.suggestion-priority {
  background: #fee2e2;
  color: #991b1b;
}

/* Planning */
.planning-week {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.week-selector {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.week-btn {
  background: #f3f4f6;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
}

.week-label {
  font-weight: 600;
  color: #1a1a1a;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
}

.day-card {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 12px;
}

.day-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.75rem 0;
}

.add-menu-btn {
  width: 100%;
  padding: 0.75rem;
  background: white;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #6b7280;
  cursor: pointer;
}

.day-balance {
  margin-top: 0.75rem;
}

.balance-indicator {
  font-size: 0.75rem;
  font-weight: 600;
}

.balance-indicator.incomplete {
  color: #f59e0b;
}

/* Reports */
.report-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.report-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 12px;
}

.report-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.25rem 0;
}

.report-description {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
}

.btn-download {
  background: #2563eb;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn {
  width: 100%;
  padding: 0.875rem;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
  .days-grid {
    grid-template-columns: 1fr;
  }
}
</style>
